import { useEffect } from 'react';
import { useQuery, useMutation, useConvexAuth } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { ChatPanel } from '@shared/components/ChatPanel';
import { CanvasEditor } from '@shared/components/CanvasEditor';
import { TopBar } from '@shared/components/TopBar';
import { EntityManagerPanel } from '@/components/EntityManagerPanel';
import { EventsManagerPanel } from '@/components/EventsManagerPanel';
import { AssetManagerPanel } from '@/components/AssetManagerPanel';
import { SettingsPanel } from '@/components/SettingsPanel';
import { GenerationModal } from '@/components/GenerationModal';
import { GlobalSearchModal } from '@/components/GlobalSearchModal';
import { WelcomeModal } from '@/components/WelcomeModal';
import { UserActivityModal } from '@/components/UserActivityModal';
import { useAppStore } from '@/store/appStore';
import { ContextWindow } from '@/components/ContextWindow';

export function EditorPage() {
  const { isAuthenticated } = useConvexAuth();
  const { activePanel, currentProjectId, setCurrentProjectId, activityUserId } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const teamId = useAppStore((s) => s.currentTeamId) ?? undefined;
  const projects = useQuery(api.projects.list, {
    accountId,
    teamId: teamId as import('../../convex/_generated/dataModel').Id<'teams'> | undefined,
  });
  const createProject = useMutation(api.projects.create);

  useEffect(() => {
    if (projects === undefined) return;
    if (projects.length > 0 && !currentProjectId) {
      setCurrentProjectId(projects[0].id);
    } else if (projects.length === 0 && isAuthenticated) {
      createProject({
        accountId,
        teamId: teamId as import('../../convex/_generated/dataModel').Id<'teams'> | undefined,
        name: 'Untitled Flyer',
      })
        .then((p) => p && setCurrentProjectId(p.id))
        .catch(console.error);
    }
  }, [projects, currentProjectId, setCurrentProjectId, createProject, isAuthenticated]);

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <TopBar />
      <div className="flex-1 min-h-0 min-w-0 w-full flex flex-col overflow-hidden">
        {/* Canvas page: chat + context side by side on left, canvas on right */}
        {activePanel === 'canvas' && (
          <div className="flex h-full w-full min-h-0 min-w-0 overflow-hidden">
            <div className="border-r border-border flex flex-shrink-0 bg-card w-[480px] min-w-[320px]">
              <div className="w-1/2 min-w-0 border-r border-border flex flex-col overflow-hidden">
                <ChatPanel />
              </div>
              <div className="w-1/2 min-w-0 flex flex-col overflow-hidden">
                <ContextWindow />
              </div>
            </div>
            <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
              <CanvasEditor />
            </div>
          </div>
        )}

        {/* All other panels: full-width content */}
        {activePanel !== 'canvas' && (
          <main className="flex-1 min-h-0 min-w-0 w-full overflow-auto flex flex-col bg-background">
            {activePanel === 'events' && <EventsManagerPanel />}
            {activePanel === 'entities' && <EntityManagerPanel />}
            {activePanel === 'assets' && <AssetManagerPanel />}
            {activePanel === 'settings' && <SettingsPanel />}
          </main>
        )}
      </div>
      <GenerationModal />
      <GlobalSearchModal />
      <WelcomeModal />
      {activityUserId && (
        <UserActivityModal
          userId={activityUserId as import('../../convex/_generated/dataModel').Id<'users'>}
          open={!!activityUserId}
          onClose={() => useAppStore.getState().setActivityUserId(null)}
        />
      )}
    </div>
  );
}
